import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Grid2,
  CircularProgress,
  TextField,
  InputAdornment,
  Link as MuiLink,
  Button,
} from "@mui/material";
import { Iconify } from '../Minimal/src/components/iconify';
import { format } from "date-fns";
import config from "../common/Config";
import "../common/commonStyles.css";
import { buildDatatablePayload, postDashboardDatatable } from "../../utils/dashboardApi";

const ManifestGenerated = () => {
  const accessToken = localStorage.getItem("accessToken");
  const token = localStorage.getItem("token");
  const userId = localStorage.getItem("userId");
  const [data, setData] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [dates, setDates] = useState([null, null]);
  const [reportrange, setReportrange] = useState('');

  const s = localStorage.getItem('startDate');
  const e = localStorage.getItem('endDate');

  const formatDate = (date) => {
    if (!date) return "0";
    const [year, month, day] = date.split("-");
    return `${day}-${month}-${year}`;
  };

  const getCurrentMonthDates = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();

    const startOfMonth = new Date(year, month, 1);
    const startDay = String(startOfMonth.getDate()).padStart(2, "0");
    const startMonth = String(startOfMonth.getMonth() + 1).padStart(2, "0");

    const endOfMonth = new Date(year, month + 1, 0);
    const endDay = String(endOfMonth.getDate()).padStart(2, "0");
    const endMonth = String(endOfMonth.getMonth() + 1).padStart(2, "0");

    const startDateFormatted = `${startDay}-${startMonth}-${year}`;
    const endDateFormatted = `${endDay}-${endMonth}-${year}`;

    return { startDateFormatted, endDateFormatted };
  };

  const { startDateFormatted, endDateFormatted } = getCurrentMonthDates();

  const handleDateChange = (value) => {
    setDates(value);
    if (value && value[0] && value[1]) {
      const formattedStartDate = format(value[0], 'yyyy-MM-dd');
      const formattedEndDate = format(value[1], 'yyyy-MM-dd');
      localStorage.setItem('startDate', formattedStartDate);
      localStorage.setItem('endDate', formattedEndDate);
      setReportrange(`${formattedStartDate} - ${formattedEndDate}`);
      setPage(0);
    }
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { response, data: json } = await postDashboardDatatable(
        token,
        'manifest_generated',
        buildDatatablePayload({
          page,
          rowsPerPage,
          startDate: s,
          endDate: e,
          searchValue,
        })
      );

      if (!response?.ok) {
        throw new Error(`Failed to fetch data: ${response?.status ?? 'unknown'}`);
      }

      if (json && Array.isArray(json.data)) {
        setData(json.data);
        setTotalCount(json.recordsTotal || json.recordsFiltered || json.data.length);
      }
    } catch (error) {
      console.error('Error fetching manifest data:', error);
    } finally {
      setLoading(false);
    }
  }, [token, page, rowsPerPage, s, e, searchValue]);

  useEffect(() => {
    if (accessToken === null) {
      window.location.href = "/react/login";
      return;
    }
    fetchData();
  }, [fetchData]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchKeyDown = (event) => {
    if (event.key === 'Enter') {
      setPage(0);
    }
  };

  const handleSearchChange = (event) => {
    setSearchValue(event.target.value);
  };

  const handleSubmit = () => {
    setPage(0);
    fetchData();
  };

  const formatRowDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    } catch (e) {
      return dateString;
    }
  };

  return (
    <>
      <Box className="page-container" >
        <Paper
          className="paper-style"
        >
          <Box sx={{ px:2, py:1.2}}>
            <Grid2
              container
              spacing={2}
              justifyContent="space-between"
              alignItems="center"
            >
              <Grid2 size={{ xs: 12, md: 2 }}>
                <Typography className="page-title">
                  Manifest Generated
                </Typography>
              </Grid2>
              <Grid2 size={{ xs: 12, md: 6 }}>
                <Box sx={{ gap: 1, display: 'flex', flexDirection: {'xs': 'column', 'md': 'row'}, justifyContent: 'space-between', alignItems: 'center' }}> 
                <TextField
                  className="input-border"
                  fullWidth
                  size="small"
                  placeholder="Search..."
                  value={searchValue}
                  onChange={handleSearchChange}
                  onKeyDown={handleSearchKeyDown}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Iconify
                          icon="eva:search-fill"
                          sx={{ color: "#212b36", width: 20, height: 20 }}
                        />
                      </InputAdornment>
                    ),
                  }}
                />
          
          
                <Typography
                  align="right"
                  sx={{
                    color: "#212b36",
                    fontSize: { xs: "0.75rem", sm: "0.875rem" },
                    whiteSpace: "nowrap",
                  }}
                >
                  From: {s ? formatDate(s) : startDateFormatted} &nbsp;
                  To: {e ? formatDate(e) : endDateFormatted}
                </Typography>
                </Box>
              </Grid2>
            </Grid2>
            </Box>
            <TableContainer
           
            >
              {loading && (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                  <CircularProgress size={40} />
                </Box>
              )}
              <Table
                size="small"
                className="table-cell-border"
                >
           
                <TableHead>
                  <TableRow className="table-cell-header">
                    <TableCell
                    width="100px"
                    >
                      #
                    </TableCell>
                    <TableCell
                    width="100px"
                    >
                      Date
                    </TableCell>
                    <TableCell
                    width="100px"
                    >
                      Manifest Number
                    </TableCell>
                    <TableCell
                    width="100px"
                    >
                      Route Number
                    </TableCell>
                    <TableCell
                    width="100px"
                    >
                      Vehicle Name
                    </TableCell>
                    <TableCell
                    width="100px"
                    >
                      Driver Name
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {!loading && data && data.length > 0 ? (
                    data.map((row, index) => (
                      <TableRow
                        key={index}
                        hover
                        className="table-cell-style"
                      >
                        <TableCell                         
                        >
                          {page * rowsPerPage + index + 1}
                        </TableCell>
                        <TableCell
                        >
                          {formatRowDate(row.date)}
                        </TableCell>
                        <TableCell
                        >
                          <MuiLink
                            href={`${config.BASE_URL}/manifest_list/mark_delivery/${row.manifest_id}`}
                            sx={{
                              color: '#212b36',
                              textDecoration: 'underline',
                              '&:hover': {
                                textDecoration: 'underline'
                              }
                            }}
                          >
                            {row.manifest_id}
                          </MuiLink>
                        </TableCell>
                        <TableCell

                        >
                          {row.route_number}
                        </TableCell>
                        <TableCell
                        >
                          {row.model_name}
                        </TableCell>
                        <TableCell           
                        >
                          {row.first_name && row.last_name ? `${row.first_name} ${row.last_name}` : ''}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : !loading ? (
                    <TableRow>
                      <TableCell
                        colSpan={6}
                        sx={{ py: 8 }}
                      >
                        <Typography align="center" sx={{ color: "#637381", fontSize: "0.875rem" }}>
                          No data found.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : null}
                </TableBody>
              </Table>
            </TableContainer>

            {!loading && (
              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[25, 50, 100]}
                className="pagination-styles"
              />
            )}
        </Paper>
      </Box>
    </>
  );
};
export default ManifestGenerated;